import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useParams } from 'react-router-dom';
import './App.css';
import BlogList from './components/BlogList';
import BlogPost from './components/BlogPost';
import SiteChrome from './components/SiteChrome';
import { getEssayBySlug, getEssayMetadata } from './data/essays';

// Static import map: each entry is a separate webpack chunk (truly lazy).
// Template-literal imports can't code-split in webpack 4 — explicit strings are required.
const ESSAY_LOADERS = {
  1: () => import('./data/content/essay-1.js'),
  2: () => import('./data/content/essay-2.js'),
  3: () => import('./data/content/essay-3.js'),
  4: () => import('./data/content/essay-4.js'),
  5: () => import('./data/content/essay-5.js'),
  6: () => import('./data/content/essay-6.js'),
  7: () => import('./data/content/essay-7.js'),
};

function EssayPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const essay = getEssayBySlug(slug);
  const [content, setContent] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const isChunkLoadError = (error) => {
    const message = error && (error.message || String(error));
    return (
      (error && error.name === 'ChunkLoadError') ||
      (typeof message === 'string' &&
        (message.includes('Loading chunk') ||
          message.includes('ChunkLoadError') ||
          message.includes('CSS_CHUNK_LOAD_FAILED')))
    );
  };

  useEffect(() => {
    if (!essay) return;
    const loader = ESSAY_LOADERS[essay.id];
    if (!loader) {
      setContent('<p>Essay content not found.</p>');
      return;
    }
    let cancelled = false;
    setLoadError(null);
    setContent(null); // show loading state

    const load = async () => {
      try {
        const module = await loader();
        if (!cancelled) setContent(module.default);
      } catch (err) {
        // Retry once: chunk failures are often transient or caused by stale cached index.html.
        try {
          const module = await loader();
          if (!cancelled) setContent(module.default);
          return;
        } catch (err2) {
          if (cancelled) return;
          // Log detail for debugging in production.
          // eslint-disable-next-line no-console
          console.error('Failed to load essay chunk:', err2);
          setLoadError(err2);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [essay]);

  const handleBackToList = () => {
    navigate('/archive');
  };

  if (!essay) {
    return (
      <SiteChrome activeNav="archive">
        <section className="essay-state-panel">
          <h1 className="essay-state-title">Essay Not Found</h1>
          <p className="essay-state-description">
            This manuscript could not be located in the current archive.
          </p>
          <button className="essay-state-action" onClick={handleBackToList} type="button">
            Return to Archive
          </button>
        </section>
      </SiteChrome>
    );
  }

  if (content === null) {
    return (
      <SiteChrome activeNav="archive">
        <section className="essay-state-panel">
          <h1 className="essay-state-title">{essay.title}</h1>
          {loadError ? (
            <>
              <p className="essay-state-description">
                {isChunkLoadError(loadError)
                  ? 'The archive updated while this page was open. Reload to continue with the latest edition.'
                  : 'The manuscript could not be loaded from disk.'}
              </p>
              <div className="essay-state-actions">
                <button
                  className="essay-state-action"
                  onClick={() => window.location.reload()}
                  type="button"
                >
                  Reload Page
                </button>
                <button
                  className="essay-state-action ghost"
                  onClick={handleBackToList}
                  type="button"
                >
                  Return to Archive
                </button>
              </div>
            </>
          ) : (
            <p className="essay-state-description">Preparing manuscript for reading...</p>
          )}
        </section>
      </SiteChrome>
    );
  }

  return (
    <BlogPost
      title={essay.title}
      date={essay.date}
      content={content}
      readMinutes={essay.readMinutes}
      author="Krish Jaiswal"
      showBackLink={true}
      onBack={handleBackToList}
    />
  );
}

function HomePage() {
  return (
    <BlogList essays={getEssayMetadata()} mode="home" />
  );
}

function ArchivePage() {
  return (
    <BlogList essays={getEssayMetadata()} mode="archive" />
  );
}

function AboutPage() {
  return (
    <SiteChrome activeNav="about">
      <section className="about-panel">
        <p className="about-kicker">Profile</p>
        <h1 className="about-title">Krish Jaiswal</h1>
        <p className="about-copy">
          He/Him. Leading MetaCognition; previously at EF, MaximemAI, Secondbrainlabs,
          Baeldung, and Antern.
        </p>
        <p className="about-copy">
          Based in Greater Kolkata, focused on engineering, systems, and AI. 19 y/o full-stack
          Java developer (Spring Boot + React), Neo4j certified, and studying Computer Science
          at BITS Pilani.
        </p>
      </section>
    </SiteChrome>
  );
}

function App() {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <Router>
      <div className={`App ${isMounted ? 'is-mounted' : ''}`}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/archive" element={<ArchivePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/essay/:slug" element={<EssayPage />} />
          <Route path="*" element={<HomePage />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
